import { useEffect, useState } from 'react'
import { fetchWand, type Wand, type WandStand } from '../lib/api'

/** Alle zehn Sekunden neu — so lange hält auch der Worker die Antwort vor. */
const TAKT_MS = 10_000

/**
 * Die Live-Wand (`/wand`): ein großer Bildschirm im Lab. Zeigt, wie viele
 * Gäste gerade mit K4ia sprechen, welche Stände frei sind und welche voll,
 * was heute am meisten besucht war. Fürs Opening gedacht: Wer die Wand sieht,
 * geht dorthin, wo gerade Platz ist.
 *
 * Kein Gespräch, keine Sitzung, keine Personen — nur Zahlen und Titel. Die
 * Fragen des Abends erscheinen nur, wenn das im Admin eingeschaltet ist, und
 * der Worker lässt dann nur kurze Sachfragen durch (db/wand-repo.ts).
 *
 * Für zwanzig Meter Abstand gebaut: große Schrift, wenig Text, viel Kontrast.
 */
export function WandPage() {
  const [wand, setWand] = useState<Wand | null>(null)
  const [fehler, setFehler] = useState(false)

  useEffect(() => {
    document.title = 'J4F VisionLab · Live'
    let aktiv = true
    const laden = () =>
      fetchWand()
        .then((w) => {
          if (!aktiv) return
          setWand(w)
          setFehler(false)
        })
        .catch(() => aktiv && setFehler(true))
    void laden()
    const t = setInterval(() => void laden(), TAKT_MS)
    return () => {
      aktiv = false
      clearInterval(t)
    }
  }, [])

  return (
    <main className="min-h-screen bg-bg px-10 py-8 text-text" data-testid="wand">
      <header className="flex items-end justify-between gap-6">
        <div>
          <p className="font-mono text-base uppercase tracking-[0.3em] text-brand">J4F VisionLab</p>
          <h1 className="mt-1 text-5xl font-bold tracking-tight">Live im Lab</h1>
        </div>
        <Uhr />
      </header>

      {fehler && !wand && (
        <p className="mt-16 text-2xl text-text-muted">Die Wand holt gerade Luft …</p>
      )}

      {wand && (
        <>
          <section className="mt-10 grid grid-cols-3 gap-6">
            <Zahl wert={wand.imGespraech} label="gerade im Gespräch mit K4ia" betont />
            <Zahl wert={wand.besucherHeute} label="Gespräche heute" />
            <Zahl wert={wand.fragenHeute} label="Fragen heute" />
          </section>

          {wand.frei.length > 0 && (
            <section className="mt-10 rounded-[2rem] bg-brand/15 px-8 py-6" aria-label="Jetzt hin">
              <p className="font-mono text-sm uppercase tracking-[0.3em] text-brand">Jetzt hin</p>
              <p className="mt-2 text-3xl font-semibold">
                {wand.frei.join(' · ')}
                <span className="ml-3 text-2xl font-normal text-text-muted">
                  — hier ist gerade Platz
                </span>
              </p>
            </section>
          )}

          <section className="mt-10" aria-label="Stände">
            <ul className="grid grid-cols-2 gap-4 xl:grid-cols-3">
              {wand.staende.map((s) => (
                <Stand key={s.id} stand={s} />
              ))}
            </ul>
          </section>

          <section className="mt-10 grid grid-cols-2 gap-6">
            <div className="rounded-[2rem] bg-surface px-8 py-6 shadow-[var(--shadow-card)]">
              <p className="font-mono text-sm uppercase tracking-[0.3em] text-text-muted">
                Beliebt heute
              </p>
              {wand.beliebt.length === 0 ? (
                <p className="mt-3 text-2xl text-text-muted">Der Abend fängt gerade an.</p>
              ) : (
                <ol className="mt-3 space-y-2 text-2xl">
                  {wand.beliebt.map((t, i) => (
                    <li key={t} className="flex gap-4">
                      <span className="w-8 font-mono text-brand">{i + 1}</span>
                      <span className="font-semibold">{t}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div className="rounded-[2rem] bg-surface px-8 py-6 shadow-[var(--shadow-card)]">
              <p className="font-mono text-sm uppercase tracking-[0.3em] text-text-muted">
                {wand.letzteFragen.length > 0 ? 'Gerade gefragt' : 'Frag K4ia'}
              </p>
              {wand.letzteFragen.length > 0 ? (
                <ul className="mt-3 space-y-2 text-xl text-text">
                  {wand.letzteFragen.slice(0, 5).map((f) => (
                    <li key={f}>„{f}“</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-2xl text-text-muted">
                  Tag am Stand ans Handy halten — oder www.k4ia.ai öffnen.
                </p>
              )}
            </div>
          </section>
        </>
      )}
    </main>
  )
}

function Zahl({ wert, label, betont = false }: { wert: number; label: string; betont?: boolean }) {
  return (
    <div
      className={`rounded-[2rem] px-8 py-6 shadow-[var(--shadow-card)] ${
        betont ? 'bg-brand text-on-accent' : 'bg-surface'
      }`}
    >
      <p className="text-7xl font-bold tabular-nums tracking-tight">{wert}</p>
      <p className={`mt-1 text-xl ${betont ? 'text-on-accent/80' : 'text-text-muted'}`}>{label}</p>
    </div>
  )
}

// Farbe trägt den Punkt, der Text bleibt in Textfarbe: So hält die Plakette
// den Kontrast in Hell und Dunkel (die Wand wird aus der Ferne gelesen).
const AUSLASTUNG_STIL: Record<WandStand['auslastung'], { punkt: string; feld: string }> = {
  frei: { punkt: 'bg-brand', feld: 'bg-brand/15' },
  ruhig: { punkt: 'bg-status-active', feld: 'bg-surface-2' },
  'gut besucht': { punkt: 'bg-status-pending', feld: 'bg-status-pending/15' },
  voll: { punkt: 'bg-status-error', feld: 'bg-status-error/15' },
}

function Stand({ stand }: { stand: WandStand }) {
  return (
    <li
      className="flex items-center justify-between gap-4 rounded-[2rem] bg-surface px-7 py-5 shadow-[var(--shadow-card)]"
      data-testid="wand-stand"
    >
      <div className="min-w-0">
        <p className="truncate text-2xl font-semibold">{stand.title}</p>
        <p className="mt-1 text-lg text-text-muted">
          {stand.heute === 1 ? 'ein Besuch heute' : `${stand.heute} Besuche heute`}
        </p>
      </div>
      <span
        className={`flex shrink-0 items-center gap-2 rounded-full px-5 py-2 text-lg font-semibold text-text ${AUSLASTUNG_STIL[stand.auslastung].feld}`}
      >
        <span
          className={`h-3 w-3 rounded-full ${AUSLASTUNG_STIL[stand.auslastung].punkt}`}
          aria-hidden="true"
        />
        {stand.auslastung}
      </span>
    </li>
  )
}

function Uhr() {
  const [jetzt, setJetzt] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setJetzt(new Date()), 1000)
    return () => clearInterval(t)
  }, [])
  return (
    <p className="font-mono text-4xl tabular-nums text-text-muted" aria-label="Uhrzeit">
      {jetzt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
    </p>
  )
}
